import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// Platform-Admin-only, global page. Same is_platform_admin() RPC check
// as CurrencyExchangeRatesPage.jsx -- every write action below also
// re-checks server-side inside its own SECURITY DEFINER function.
function useAdminAccess() {
  const [allowed, setAllowed] = useState(null)
  useEffect(() => {
    let cancelled = false
    supabase.rpc('is_platform_admin').then(({ data }) => { if (!cancelled) setAllowed(!!data) })
    return () => { cancelled = true }
  }, [])
  return allowed
}

const STATUS_STYLE = {
  trial:     { bg: '#DBEAFE', color: '#1D4ED8' },
  active:    { bg: '#DCFCE7', color: '#15803D' },
  past_due:  { bg: '#FEF3C7', color: '#92400E' },
  expired:   { bg: '#FEF2F2', color: '#DC2626' },
  suspended: { bg: '#FEF2F2', color: '#DC2626' },
  cancelled: { bg: '#F3F4F6', color: '#6B7280' },
}
const REQUEST_STATUS_STYLE = {
  new:       { bg: '#DBEAFE', color: '#1D4ED8' },
  contacted: { bg: '#FEF3C7', color: '#92400E' },
  approved:  { bg: '#DCFCE7', color: '#15803D' },
  rejected:  { bg: '#FEF2F2', color: '#DC2626' },
  converted: { bg: '#F3F4F6', color: '#6B7280' },
}

function fmt(d) { return d ? new Date(d).toLocaleDateString() : '—' }
function daysUntil(d) {
  if (!d) return null
  return Math.ceil((new Date(d) - new Date()) / 86400000)
}

const btn = { fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: '1px solid #DDD9D0', background: '#fff', color: '#374151', cursor: 'pointer' }
const tabBtn = (active) => ({ padding: '8px 14px', fontSize: 12, fontWeight: 700, background: 'none', border: 'none', borderBottom: active ? '2px solid #1A7A4A' : '2px solid transparent', color: active ? '#1A7A4A' : '#9A948C', cursor: 'pointer' })

export default function SubscriptionManagementPage() {
  const allowed = useAdminAccess()
  const [companies, setCompanies] = useState([])
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [emailDraft, setEmailDraft] = useState('')
  const [savingEmail, setSavingEmail] = useState(false)
  const [view, setView] = useState('customers') // 'customers' | 'pricing'

  useEffect(() => { if (allowed) { loadCompanies(); loadRequests(); loadRecipientEmail() } }, [allowed])

  // Companies = organizations that have a subscription record (per the
  // corrected requirement: NOT every row in `organizations`). A
  // subscription only exists for orgs that went through
  // start_organization_trial() -- i.e. the new commercial flow, or the
  // pre-existing production org that was explicitly seeded.
  async function loadCompanies() {
    setLoading(true)
    const { data: subs } = await supabase.from('organization_subscriptions').select('*, organizations(id, name)')
    const merged = await Promise.all((subs || []).map(async (sub) => {
      const { data: status } = await supabase.rpc('get_organization_subscription_status', { p_organization_id: sub.organization_id })
      const { count: userCount } = await supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('organization_id', sub.organization_id)
      return { sub, org: sub.organizations, computedStatus: status, userCount: userCount || 0 }
    }))
    setCompanies(merged)
    setLoading(false)
  }

  async function loadRequests() {
    const { data } = await supabase.from('trial_requests').select('*').order('created_at', { ascending: false })
    setRequests(data || [])
  }

  async function loadRecipientEmail() {
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'trial_request_recipient_email').maybeSingle()
    setRecipientEmail(data?.value || '')
    setEmailDraft(data?.value || '')
  }

  async function saveRecipientEmail() {
    setSavingEmail(true)
    const { error } = await supabase.from('app_settings').update({ value: emailDraft, updated_at: new Date().toISOString() }).eq('key', 'trial_request_recipient_email')
    setSavingEmail(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setRecipientEmail(emailDraft)
    setMsg({ type: 'success', text: 'Recipient email updated.' })
  }

  async function updateRequestStatus(id, status) {
    const notes = status === 'rejected' ? window.prompt('Reason (optional):') : null
    const { error } = await supabase.rpc('admin_update_trial_request', { p_request_id: id, p_status: status, p_notes: notes })
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    loadRequests()
  }

  // Approve = the real onboarding flow: creates the organization + initial
  // user (via the existing Invite-Only onboard_cost_control_engineer()),
  // starts the trial, links the request, marks it CONVERTED -- all
  // server-side in one call.
  async function approveRequest(id) {
    if (!window.confirm('Approve this request? This will create a new organization, invite the requester as the initial user, and start their trial.')) return
    const { error } = await supabase.rpc('admin_approve_trial_request', { p_request_id: id })
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: 'Organization created and trial started.' })
    loadRequests()
    loadCompanies()
  }

  if (allowed === null) return <div style={{ padding: 60, textAlign: 'center', color: '#9A948C', fontSize: 13 }}>Loading…</div>
  if (!allowed) return <div style={{ padding: 60, textAlign: 'center', color: '#9A948C', fontSize: 13 }}>Access denied — you don't have permission to view this page.</div>

  const selected = companies.find(c => c.org?.id === selectedOrgId)

  return (
    <div className="min-h-screen font-sans" style={{ background: '#F5F1EA' }}>
      <div style={{ background: '#F5F1EA', borderBottom: '1px solid #DDD9D0', padding: '20px 32px' }}>
        <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.14em', color: '#9A948C', marginBottom: 4 }}>Setup</p>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C1A18' }}>Subscription Management</h1>
      </div>

      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        {msg && (
          <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`, fontSize: 12, color: msg.type === 'success' ? '#15803d' : '#991b1b', display: 'flex', justifyContent: 'space-between' }}>
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #DDD9D0' }}>
          <button onClick={() => setView('customers')} style={tabBtn(view === 'customers')}>Customers & Requests</button>
          <button onClick={() => setView('pricing')} style={tabBtn(view === 'pricing')}>Pricing</button>
        </div>

        {view === 'pricing' && <PricingPanel setMsg={setMsg} />}

        {view === 'customers' && (<>
        {/* Trial Requests */}
        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#1C1A18', marginBottom: 12 }}>Trial Requests</h2>
        <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, overflow: 'hidden', marginBottom: 32 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F5F1EA', borderBottom: '1px solid #DDD9D0' }}>
                {['Name', 'Company', 'Project', 'Email', 'Status', 'Received', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#9A948C' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#9A948C' }}>No trial requests yet.</td></tr>
              ) : requests.map(r => {
                const style = REQUEST_STATUS_STYLE[r.status] || REQUEST_STATUS_STYLE.new
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #F5F1EA' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1C1A18' }}>{r.full_name}</td>
                    <td style={{ padding: '10px 14px', color: '#6B655C' }}>{r.company_name}</td>
                    <td style={{ padding: '10px 14px', color: '#6B655C' }}>{r.project_name}</td>
                    <td style={{ padding: '10px 14px', color: '#6B655C' }}>{r.email}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: style.bg, color: style.color }}>{r.status.toUpperCase()}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#9A948C', fontSize: 11 }}>{fmt(r.created_at)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.status === 'new' && <button onClick={() => updateRequestStatus(r.id, 'contacted')} style={btn}>Mark Contacted</button>}
                        {r.status !== 'converted' && r.status !== 'rejected' && (
                          <button onClick={() => approveRequest(r.id)} style={{ ...btn, color: '#1A7A4A', fontWeight: 700 }}>Approve</button>
                        )}
                        {r.status !== 'rejected' && r.status !== 'converted' && <button onClick={() => updateRequestStatus(r.id, 'rejected')} style={{ ...btn, color: '#A32D2D' }}>Reject</button>}
                        {r.organization_id && <span style={{ fontSize: 10, color: '#9A948C', alignSelf: 'center' }}>→ {companies.find(c => c.org?.id === r.organization_id)?.org?.name}</span>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Trial Request Recipient Email */}
        <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, padding: 20, marginBottom: 32 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1C1A18', marginBottom: 4 }}>Trial Request Recipient Email</h2>
          <div style={{ display: 'flex', gap: 8, maxWidth: 420, marginTop: 8 }}>
            <input type="email" value={emailDraft} onChange={e => setEmailDraft(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12 }} />
            <button onClick={saveRecipientEmail} disabled={savingEmail || emailDraft === recipientEmail} style={btn}>
              {savingEmail ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Customers (Sidebar + Detail) */}
        <h2 style={{ fontSize: 15, fontWeight: 800, color: '#1C1A18', marginBottom: 12 }}>Customers</h2>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9A948C' }}>Loading…</div>
        ) : companies.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9A948C', background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12 }}>
            No customers yet. Approve a trial request above to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ width: 280, flexShrink: 0, background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, overflow: 'hidden' }}>
              {companies.map(c => {
                const style = STATUS_STYLE[c.computedStatus] || STATUS_STYLE.trial
                const isSelected = c.org?.id === selectedOrgId
                return (
                  <button key={c.org?.id} onClick={() => setSelectedOrgId(c.org?.id)}
                    style={{ width: '100%', textAlign: 'left', padding: '14px 16px', border: 'none', borderBottom: '1px solid #F5F1EA', background: isSelected ? '#F5F1EA' : '#fff', cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1C1A18', marginBottom: 4 }}>{c.org?.name}</div>
                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, background: style.bg, color: style.color }}>{c.computedStatus?.toUpperCase()}</span>
                    <div style={{ fontSize: 10, color: '#9A948C', marginTop: 4 }}>{c.userCount} user{c.userCount !== 1 ? 's' : ''}</div>
                  </button>
                )
              })}
            </div>

            <div style={{ flex: 1 }}>
              {!selected ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9A948C', background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12 }}>
                  Select a customer to view details.
                </div>
              ) : (
                <CompanyDetail company={selected} onChanged={() => { loadCompanies() }} setMsg={setMsg} />
              )}
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  )
}

function CompanyDetail({ company, onChanged, setMsg }) {
  const [tab, setTab] = useState('overview')
  const [users, setUsers] = useState([])
  const [payments, setPayments] = useState([])
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentForm, setPaymentForm] = useState({ user_id: '', amount: '', currency: 'EGP', payment_date: '', period_start: '', period_end: '', payment_method: '', reference: '', notes: '' })
  const [billing, setBilling] = useState(null)
  const [planTiers, setPlanTiers] = useState([])
  const [suggestion, setSuggestion] = useState(null)
  const [busy, setBusy] = useState(false)

  const { org, sub, computedStatus, userCount } = company
  const style = STATUS_STYLE[computedStatus] || STATUS_STYLE.trial
  const remaining = daysUntil(sub?.status === 'trial' ? sub?.trial_ends_at : sub?.current_period_end)

  useEffect(() => { loadUsers(); loadPayments(); loadBilling(); loadPlanTiers() }, [org?.id])

  // The suggested amount comes from the database (get_expected_payment); it is only a
  // suggestion -- the amount field stays editable.
  useEffect(() => {
    if (!showPaymentForm || !paymentForm.period_start || !paymentForm.period_end) { setSuggestion(null); return }
    let cancelled = false
    supabase.rpc('get_expected_payment', {
      p_organization_id: org.id,
      p_user_id: paymentForm.user_id || null,
      p_period_start: paymentForm.period_start,
      p_period_end: paymentForm.period_end,
    }).then(({ data, error }) => { if (!cancelled) setSuggestion(error ? null : data) })
    return () => { cancelled = true }
  }, [showPaymentForm, org?.id, paymentForm.user_id, paymentForm.period_start, paymentForm.period_end])

  async function loadBilling() {
    const { data } = await supabase.rpc('get_org_billing_overview', { p_organization_id: org.id })
    setBilling(data || null)
  }
  async function loadPlanTiers() {
    const { data } = await supabase.from('pricing_plan_tiers').select('*').eq('plan_code', sub?.plan_code).eq('is_active', true).order('sort_order')
    setPlanTiers(data || [])
  }

  async function loadUsers() {
    const { data } = await supabase.from('user_profiles').select('id, full_name, email, role, is_active').eq('organization_id', org.id)
    setUsers(data || [])
  }
  async function loadPayments() {
    const { data } = await supabase.from('subscription_payments').select('*').eq('organization_id', org.id).order('payment_date', { ascending: false })
    setPayments(data || [])
  }

  async function runAction(fn, args = {}) {
    setBusy(true)
    const { error } = await supabase.rpc(fn, { p_organization_id: org.id, ...args })
    setBusy(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: 'Done.' })
    onChanged()
  }

  async function submitPayment(e) {
    e.preventDefault()
    setBusy(true)
    const { error } = await supabase.rpc('admin_record_payment', {
      p_organization_id: org.id,
      p_amount: parseFloat(paymentForm.amount),
      p_currency: paymentForm.currency,
      p_payment_date: paymentForm.payment_date,
      p_period_start: paymentForm.period_start,
      p_period_end: paymentForm.period_end,
      p_payment_method: paymentForm.payment_method || null,
      p_reference: paymentForm.reference || null,
      p_notes: paymentForm.notes || null,
      p_user_id: paymentForm.user_id || null,
    })
    setBusy(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: 'Payment recorded.' })
    setShowPaymentForm(false)
    setPaymentForm({ user_id: '', amount: '', currency: 'EGP', payment_date: '', period_start: '', period_end: '', payment_method: '', reference: '', notes: '' })
    loadPayments()
    loadBilling()
    onChanged()
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid #DDD9D0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#1C1A18' }}>{org.name}</h3>
            <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: style.bg, color: style.color, marginTop: 4, display: 'inline-block' }}>
              {computedStatus?.toUpperCase()}
            </span>
            {remaining != null && (
              <span style={{ fontSize: 11, color: remaining < 0 ? '#DC2626' : remaining <= 7 ? '#D97706' : '#9A948C', marginLeft: 8 }}>
                {remaining < 0 ? `Expired ${Math.abs(remaining)} day(s) ago` : `${remaining} day(s) remaining`}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {computedStatus === 'trial' && (
              <>
                <button disabled={busy} onClick={() => runAction('admin_extend_trial', { p_days: 7 })} style={btn}>+7 Days</button>
                <button disabled={busy} onClick={() => runAction('admin_extend_trial', { p_days: 30 })} style={btn}>+1 Month</button>
              </>
            )}
            <button disabled={busy} onClick={() => runAction('admin_grant_free_month')} style={btn}>Grant Free Month</button>
            <button disabled={busy} onClick={() => setShowPaymentForm(v => !v)} style={{ ...btn, color: '#1A7A4A', fontWeight: 700 }}>Record Payment</button>
            {computedStatus !== 'suspended' && computedStatus !== 'cancelled' && (
              <button disabled={busy} onClick={() => { const r = window.prompt('Suspension reason:'); if (r != null) runAction('admin_suspend_subscription', { p_reason: r }) }} style={{ ...btn, color: '#A32D2D' }}>Suspend</button>
            )}
            {computedStatus === 'suspended' && (
              <button disabled={busy} onClick={() => runAction('admin_reactivate_subscription')} style={{ ...btn, color: '#1A7A4A' }}>Reactivate</button>
            )}
            {computedStatus !== 'cancelled' && (
              <button disabled={busy} onClick={() => { const r = window.prompt('Cancellation reason:'); if (r != null) runAction('admin_cancel_subscription', { p_reason: r }) }} style={{ ...btn, color: '#A32D2D' }}>Cancel</button>
            )}
          </div>
        </div>
      </div>

      {showPaymentForm && (
        <form onSubmit={submitPayment} style={{ padding: 20, borderBottom: '1px solid #DDD9D0', background: '#F5F1EA', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Payment For">
            <select value={paymentForm.user_id} onChange={e => setPaymentForm(f => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
              <option value="">Whole company</option>
              {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
            </select>
          </Field>
          <Field label="Amount" required><input required type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} /></Field>
          <Field label="Currency"><input value={paymentForm.currency} onChange={e => setPaymentForm(f => ({ ...f, currency: e.target.value }))} style={inputStyle} /></Field>
          <Field label="Payment Date" required><input required type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))} style={inputStyle} /></Field>
          <Field label="Paid From" required><input required type="date" value={paymentForm.period_start} onChange={e => setPaymentForm(f => ({ ...f, period_start: e.target.value }))} style={inputStyle} /></Field>
          <Field label="Paid To" required><input required type="date" value={paymentForm.period_end} onChange={e => setPaymentForm(f => ({ ...f, period_end: e.target.value }))} style={inputStyle} /></Field>
          <Field label="Payment Method"><input value={paymentForm.payment_method} onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))} placeholder="Bank Transfer" style={inputStyle} /></Field>
          <Field label="Reference"><input value={paymentForm.reference} onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))} placeholder="INV-001" style={inputStyle} /></Field>
          <Field label="Notes"><input value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} style={inputStyle} /></Field>
          {suggestion && suggestion.expected != null && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#374151' }}>
              Suggested: <b>{suggestion.expected} {suggestion.currency}</b> ({suggestion.months} month{suggestion.months === 1 ? '' : 's'} × {suggestion.unit_price}{suggestion.basis === 'per_user_all_active' ? ` × ${suggestion.users} active users` : ''})
              {' '}<button type="button" onClick={() => setPaymentForm(f => ({ ...f, amount: String(suggestion.expected), currency: suggestion.currency || f.currency }))} style={{ ...btn, padding: '2px 8px' }}>Use</button>
            </div>
          )}
          {suggestion && suggestion.expected == null && suggestion.basis === 'no_price' && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#92400E' }}>No price per user is set for this company yet (set it in the Billing tab or on the plan in Pricing).</div>
          )}
          <div style={{ alignSelf: 'end' }}>
            <button type="submit" disabled={busy} style={{ ...btn, color: '#fff', background: '#1A7A4A', border: 'none', width: '100%' }}>{busy ? '…' : 'Save Payment'}</button>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: 4, padding: '0 20px', borderBottom: '1px solid #DDD9D0' }}>
        {['overview', 'users', 'subscription', 'billing', 'payments'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={tabBtn(tab === t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
            <Row label="Status" value={computedStatus?.toUpperCase()} />
            <Row label="Plan" value={sub?.plan_code} />
            <Row label="Users" value={userCount} />
            <Row label="Access Until" value={fmt(sub?.status === 'trial' ? sub?.trial_ends_at : sub?.current_period_end)} />
          </div>
        )}

        {tab === 'users' && (
          users.length === 0 ? <p style={{ color: '#9A948C', fontSize: 12 }}>No users yet.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ borderBottom: '1px solid #DDD9D0' }}>{['Name', 'Email', 'Role', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, color: '#9A948C', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>{users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #F5F1EA' }}>
                  <td style={{ padding: 8, fontWeight: 600 }}>{u.full_name}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{u.email}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{u.role}</td>
                  <td style={{ padding: 8 }}>{u.is_active ? 'Active' : 'Inactive'}</td>
                </tr>
              ))}</tbody>
            </table>
          )
        )}

        {tab === 'subscription' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
            <Row label="Trial Start" value={fmt(sub?.trial_started_at)} />
            <Row label="Trial End" value={fmt(sub?.trial_ends_at)} />
            <Row label="Subscription Start" value={fmt(sub?.subscription_started_at)} />
            <Row label="Current Period Start" value={fmt(sub?.current_period_start)} />
            <Row label="Current Period End" value={fmt(sub?.current_period_end)} />
            <Row label="Next Billing Date" value={fmt(sub?.next_billing_date)} />
            {sub?.suspension_reason && <Row label="Suspension Reason" value={sub.suspension_reason} />}
            {sub?.cancellation_reason && <Row label="Cancellation Reason" value={sub.cancellation_reason} />}
          </div>
        )}

        {tab === 'billing' && (
          <BillingTab org={org} billing={billing} planTiers={planTiers} onSaved={() => { loadBilling(); onChanged() }} setMsg={setMsg} />
        )}

        {tab === 'payments' && (
          payments.length === 0 ? <p style={{ color: '#9A948C', fontSize: 12 }}>No payments recorded yet.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ borderBottom: '1px solid #DDD9D0' }}>{['Date', 'Amount', 'For', 'Period', 'Method', 'Reference'].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, color: '#9A948C', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>{payments.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid #F5F1EA' }}>
                  <td style={{ padding: 8 }}>{fmt(p.payment_date)}</td>
                  <td style={{ padding: 8, fontWeight: 700 }}>{p.amount} {p.currency}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{p.user_id ? (users.find(u => u.id === p.user_id)?.full_name || 'User') : 'Company'}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{fmt(p.period_start)} → {fmt(p.period_end)}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{p.payment_method || '—'}</td>
                  <td style={{ padding: 8, color: '#6B655C' }}>{p.reference || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )
        )}
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#9A948C', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#1C1A18' }}>{value ?? '—'}</div>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#6B655C', marginBottom: 3 }}>{label}{required && ' *'}</label>
      {children}
    </div>
  )
}

const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 12 }


// =======================================================================
// Billing tab of one company: how it is billed (per user, or by tier), its
// usage against the tier limits, and who has paid. Every number here comes
// from get_org_billing_overview() -- nothing is calculated in React.
// =======================================================================
const BASIS_LABEL = {
  tier: 'tier price',
  plan_price: 'plan price (no tier assigned yet)',
  per_user: 'price per user × active users',
  no_price: 'no price per user set yet',
}

function BillingTab({ org, billing, planTiers, onSaved, setMsg }) {
  const [mode, setMode] = useState('tier')
  const [tierId, setTierId] = useState('')
  const [override, setOverride] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!billing) return
    setMode(billing.billing_mode || 'tier')
    setTierId(billing.tier?.id || '')
    setOverride(billing.unit_price_is_override && billing.unit_price != null ? String(billing.unit_price) : '')
  }, [billing])

  if (!billing) return <p style={{ color: '#9A948C', fontSize: 12 }}>Loading billing…</p>

  const usage = billing.usage || {}
  const cur = billing.currency

  async function save() {
    setSaving(true)
    const { error } = await supabase.rpc('admin_set_org_billing', {
      p_organization_id: org.id,
      p_billing_mode: mode,
      p_tier_id: mode === 'tier' ? (tierId || null) : null,
      p_price_per_user_override: mode === 'per_user' && override !== '' ? parseFloat(override) : null,
    })
    setSaving(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: 'Billing setup saved.' })
    onSaved()
  }

  const overStyle = { color: '#DC2626', fontWeight: 700 }

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 18 }}>
        <Field label="Billed">
          <select value={mode} onChange={e => setMode(e.target.value)} style={inputStyle}>
            <option value="tier">By tier (fixed price for the company)</option>
            <option value="per_user">Per user</option>
          </select>
        </Field>
        {mode === 'tier' ? (
          <Field label="Tier">
            <select value={tierId} onChange={e => setTierId(e.target.value)} style={inputStyle}>
              <option value="">No tier (use the plan price)</option>
              {planTiers.map(t => (
                <option key={t.id} value={t.id}>
                  {t.tier_name} — {t.max_users != null ? `${t.max_users} users` : 'any users'}{t.max_projects != null ? `, ${t.max_projects} projects` : ''} — {t.monthly_price}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Price per user (special to this company)">
            <input type="number" min="0" step="0.01" value={override} onChange={e => setOverride(e.target.value)}
              placeholder="blank = plan price" style={inputStyle} />
          </Field>
        )}
        <div />
        <button type="button" disabled={saving} onClick={save} style={{ ...btn, color: '#fff', background: '#1A7A4A', border: 'none' }}>{saving ? '…' : 'Save'}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <Row label="Expected per month" value={billing.expected_monthly != null ? `${billing.expected_monthly} ${cur} — ${BASIS_LABEL[billing.basis] || billing.basis}` : BASIS_LABEL[billing.basis] || '—'} />
        <Row label={billing.billing_mode === 'per_user' ? 'Price per user' : 'Tier'} value={billing.billing_mode === 'per_user'
          ? (billing.unit_price != null ? `${billing.unit_price} ${cur}${billing.unit_price_is_override ? ' (special to this company)' : ' (plan price)'}` : '—')
          : (billing.tier ? `${billing.tier.name} (${billing.tier.max_users != null ? billing.tier.max_users + ' users' : 'any users'}${billing.tier.max_projects != null ? ', ' + billing.tier.max_projects + ' projects' : ''})` : 'None')} />
        <Row label="Active users" value={<span style={usage.over_users ? overStyle : undefined}>{usage.active_users}{usage.max_users != null ? ` of ${usage.max_users}` : ''}{usage.over_users ? ' — over the limit' : ''}</span>} />
        <Row label="Projects" value={<span style={usage.over_projects ? overStyle : undefined}>{usage.projects}{usage.max_projects != null ? ` of ${usage.max_projects}` : ''}{usage.over_projects ? ' — over the limit' : ''}</span>} />
      </div>
      <p style={{ fontSize: 11, color: '#9A948C', margin: '0 0 14px' }}>Limits are shown for information only — nothing is blocked.</p>

      {billing.billing_mode === 'tier' && (
        <Row label="Company payments paid through" value={fmt(billing.company_paid_through)} />
      )}

      {billing.billing_mode === 'per_user' && (
        (billing.users || []).length === 0 ? <p style={{ color: '#9A948C', fontSize: 12 }}>No active users.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: '1px solid #DDD9D0' }}>{['User', 'Monthly price', 'Paid through', 'Total paid'].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, color: '#9A948C', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
            <tbody>{billing.users.map(u => (
              <tr key={u.user_id} style={{ borderBottom: '1px solid #F5F1EA' }}>
                <td style={{ padding: 8, fontWeight: 600 }}>{u.full_name || u.email}</td>
                <td style={{ padding: 8 }}>{u.monthly_price != null ? `${u.monthly_price} ${cur}` : '—'}</td>
                <td style={{ padding: 8, color: u.paid_through ? '#15803D' : '#9A948C' }}>{u.paid_through ? fmt(u.paid_through) : 'No payment recorded'}</td>
                <td style={{ padding: 8 }}>{u.total_paid} {cur}</td>
              </tr>
            ))}</tbody>
          </table>
        )
      )}
    </div>
  )
}

// =======================================================================
// Pricing tab: everything the landing page shows about plans and prices.
// The landing reads it through get_landing_pricing(); the tables are
// written here directly (platform admin only, enforced by row-level security).
// =======================================================================
// Turns a database refusal from the pricing tables into a sentence the admin can act on.
function pricingError(error) {
  const m = (error && error.message) || ''
  if (/row-level security|permission denied/i.test(m)) return 'You are not allowed to change pricing (platform admin only).'
  if (/duplicate key|unique constraint/i.test(m)) return 'A tier with this name already exists in this plan.'
  if (/max_users_check|max_projects_check/i.test(m)) return 'Limits must be 1 or more (or blank for no limit).'
  if (/monthly_price_check|price_per_user_check/i.test(m)) return 'Prices cannot be negative.'
  return m
}

const DISPLAY_MODES = [
  { value: 'fixed', label: 'One fixed price' },
  { value: 'tiers_table', label: 'Tiers table (each tier with its price)' },
  { value: 'from_price', label: '"From X" (the lowest tier)' },
  { value: 'custom_text', label: 'Custom text (for example: Contact us)' },
  { value: 'hidden', label: 'No price (features only)' },
]

function PricingPanel({ setMsg }) {
  const [plans, setPlans] = useState(null)
  const [tiers, setTiers] = useState([])

  useEffect(() => { load() }, [])
  async function load() {
    const [p, t] = await Promise.all([
      supabase.from('pricing_plans').select('*').order('sort_order').order('code'),
      supabase.from('pricing_plan_tiers').select('*').order('sort_order').order('monthly_price'),
    ])
    if (p.error) { setMsg({ type: 'error', text: pricingError(p.error) }); return }
    setPlans(p.data || [])
    setTiers(t.data || [])
  }

  if (plans === null) return <p style={{ color: '#9A948C', fontSize: 13 }}>Loading plans…</p>
  return (
    <div>
      <p style={{ fontSize: 12, color: '#6B655C', marginBottom: 16 }}>
        These settings control the Pricing section of the landing page. The <b>Free Trial</b> card is always shown; its length is the trial days of the first active plan.
        Hiding a plan only removes its card from the landing page — existing customers and their subscriptions are not affected.
      </p>
      {plans.map(pl => (
        <PlanEditor key={pl.code} plan={pl} tiers={tiers.filter(t => t.plan_code === pl.code)} onChanged={load} setMsg={setMsg} />
      ))}
    </div>
  )
}

function PlanEditor({ plan, tiers, onChanged, setMsg }) {
  const toForm = (pl) => ({
    name: pl.name || '', tagline: pl.tagline || '', cta_label: pl.cta_label || '', badge_text: pl.badge_text || '',
    monthly_price: pl.monthly_price ?? '', currency: pl.currency || '', trial_days: pl.trial_days ?? '', sort_order: pl.sort_order ?? 0,
    price_per_user: pl.price_per_user ?? '', price_display_mode: pl.price_display_mode || 'fixed', price_custom_text: pl.price_custom_text || '',
    features: (pl.features || []).join('\n'),
    is_active: !!pl.is_active, show_on_landing: !!pl.show_on_landing, highlighted: !!pl.highlighted,
  })
  const [form, setForm] = useState(() => toForm(plan))
  const [saving, setSaving] = useState(false)
  useEffect(() => { setForm(toForm(plan)) }, [plan])
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  async function save() {
    setSaving(true)
    const { error } = await supabase.from('pricing_plans').update({
      name: form.name.trim(),
      tagline: form.tagline.trim() || null,
      cta_label: form.cta_label.trim() || 'Start Free Trial',
      badge_text: form.badge_text.trim() || null,
      monthly_price: Number(form.monthly_price),
      currency: form.currency.trim().toUpperCase(),
      trial_days: parseInt(form.trial_days, 10),
      sort_order: parseInt(form.sort_order, 10) || 0,
      price_per_user: form.price_per_user === '' ? null : Number(form.price_per_user),
      price_display_mode: form.price_display_mode,
      price_custom_text: form.price_custom_text.trim() || null,
      features: form.features.split('\n').map(x => x.trim()).filter(Boolean),
      is_active: form.is_active, show_on_landing: form.show_on_landing, highlighted: form.highlighted,
      updated_at: new Date().toISOString(),
    }).eq('code', plan.code)
    setSaving(false)
    if (error) { setMsg({ type: 'error', text: pricingError(error) }); return }
    setMsg({ type: 'success', text: `Plan "${form.name}" saved.` })
    onChanged()
  }

  const check = (k, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
      <input type="checkbox" checked={form[k]} onChange={set(k)} /> {label}
    </label>
  )

  return (
    <div style={{ background: '#fff', border: '1px solid #DDD9D0', borderRadius: 12, marginBottom: 24, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #DDD9D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: '#1C1A18' }}>{plan.name} <span style={{ fontSize: 11, color: '#9A948C', fontWeight: 600 }}>({plan.code})</span></h3>
        <button type="button" disabled={saving} onClick={save} style={{ ...btn, color: '#fff', background: '#1A7A4A', border: 'none' }}>{saving ? '…' : 'Save plan'}</button>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 22, marginBottom: 14, flexWrap: 'wrap' }}>
          {check('show_on_landing', 'Show on the landing page')}
          {check('is_active', 'Active (can start trials)')}
          {check('highlighted', 'Highlighted card')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <Field label="Name"><input value={form.name} onChange={set('name')} style={inputStyle} /></Field>
          <Field label="Price per month"><input type="number" min="0" step="0.01" value={form.monthly_price} onChange={set('monthly_price')} style={inputStyle} /></Field>
          <Field label="Currency"><input value={form.currency} onChange={set('currency')} style={inputStyle} /></Field>
          <Field label="Trial days"><input type="number" min="0" value={form.trial_days} onChange={set('trial_days')} style={inputStyle} /></Field>
          <Field label="Tagline"><input value={form.tagline} onChange={set('tagline')} style={inputStyle} /></Field>
          <Field label="Button text"><input value={form.cta_label} onChange={set('cta_label')} style={inputStyle} /></Field>
          <Field label="Badge text (blank = Recommended)"><input value={form.badge_text} onChange={set('badge_text')} style={inputStyle} /></Field>
          <Field label="Order on the page"><input type="number" value={form.sort_order} onChange={set('sort_order')} style={inputStyle} /></Field>
          <Field label="How the price is shown">
            <select value={form.price_display_mode} onChange={set('price_display_mode')} style={inputStyle}>
              {DISPLAY_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          {form.price_display_mode === 'custom_text' && (
            <Field label="Custom price text"><input value={form.price_custom_text} onChange={set('price_custom_text')} placeholder="Contact us for pricing" style={inputStyle} /></Field>
          )}
          <Field label="Default price per user (per-user companies)"><input type="number" min="0" step="0.01" value={form.price_per_user} onChange={set('price_per_user')} style={inputStyle} /></Field>
        </div>
        <Field label="Features (one per line)">
          <textarea rows={5} value={form.features} onChange={set('features')} style={{ ...inputStyle, fontFamily: 'inherit' }} />
        </Field>

        <h4 style={{ fontSize: 13, fontWeight: 800, color: '#1C1A18', margin: '22px 0 4px' }}>Tiers</h4>
        <p style={{ fontSize: 11, color: '#9A948C', margin: '0 0 8px' }}>A tier is a fixed monthly price for the whole company, by number of users and/or projects. Leave a limit blank for “no limit”.</p>
        <TierTable plan={plan} tiers={tiers} onChanged={onChanged} setMsg={setMsg} />
      </div>
    </div>
  )
}

function TierTable({ plan, tiers, onChanged, setMsg }) {
  const blank = { tier_name: '', max_users: '', max_projects: '', monthly_price: '', sort_order: tiers.length + 1 }
  const [draft, setDraft] = useState(blank)
  const [adding, setAdding] = useState(false)

  async function add(e) {
    e.preventDefault()
    setAdding(true)
    const { error } = await supabase.from('pricing_plan_tiers').insert({
      plan_code: plan.code, tier_name: draft.tier_name.trim(),
      max_users: draft.max_users === '' ? null : parseInt(draft.max_users, 10),
      max_projects: draft.max_projects === '' ? null : parseInt(draft.max_projects, 10),
      monthly_price: Number(draft.monthly_price), sort_order: parseInt(draft.sort_order, 10) || 0,
    })
    setAdding(false)
    if (error) { setMsg({ type: 'error', text: pricingError(error) }); return }
    setMsg({ type: 'success', text: 'Tier added.' })
    setDraft({ ...blank, sort_order: tiers.length + 2 })
    onChanged()
  }

  const th = (h) => <th key={h} style={{ textAlign: 'left', padding: 6, color: '#9A948C', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ borderBottom: '1px solid #DDD9D0' }}>{['Name', 'Max users', 'Max projects', 'Price / month', 'Order', 'On landing', 'Active', ''].map(th)}</tr></thead>
        <tbody>
          {tiers.map(t => <TierRow key={t.id} tier={t} onChanged={onChanged} setMsg={setMsg} />)}
          {tiers.length === 0 && <tr><td colSpan={8} style={{ padding: 8, color: '#9A948C' }}>No tiers yet.</td></tr>}
        </tbody>
      </table>
      <form onSubmit={add} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 70px auto', gap: 8, marginTop: 10, alignItems: 'end' }}>
        <Field label="New tier name" required><input required value={draft.tier_name} onChange={e => setDraft(d => ({ ...d, tier_name: e.target.value }))} style={inputStyle} /></Field>
        <Field label="Max users"><input type="number" min="1" value={draft.max_users} onChange={e => setDraft(d => ({ ...d, max_users: e.target.value }))} style={inputStyle} /></Field>
        <Field label="Max projects"><input type="number" min="1" value={draft.max_projects} onChange={e => setDraft(d => ({ ...d, max_projects: e.target.value }))} style={inputStyle} /></Field>
        <Field label="Price / month" required><input required type="number" min="0" step="0.01" value={draft.monthly_price} onChange={e => setDraft(d => ({ ...d, monthly_price: e.target.value }))} style={inputStyle} /></Field>
        <Field label="Order"><input type="number" value={draft.sort_order} onChange={e => setDraft(d => ({ ...d, sort_order: e.target.value }))} style={inputStyle} /></Field>
        <button type="submit" disabled={adding} style={{ ...btn, color: '#fff', background: '#1A7A4A', border: 'none' }}>{adding ? '…' : '+ Add tier'}</button>
      </form>
    </div>
  )
}

function TierRow({ tier, onChanged, setMsg }) {
  const [f, setF] = useState({ tier_name: tier.tier_name, max_users: tier.max_users ?? '', max_projects: tier.max_projects ?? '', monthly_price: tier.monthly_price, sort_order: tier.sort_order, show_on_landing: tier.show_on_landing, is_active: tier.is_active })
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  async function save() {
    setBusy(true)
    const { error } = await supabase.from('pricing_plan_tiers').update({
      tier_name: String(f.tier_name).trim(),
      max_users: f.max_users === '' ? null : parseInt(f.max_users, 10),
      max_projects: f.max_projects === '' ? null : parseInt(f.max_projects, 10),
      monthly_price: Number(f.monthly_price), sort_order: parseInt(f.sort_order, 10) || 0,
      show_on_landing: f.show_on_landing, is_active: f.is_active, updated_at: new Date().toISOString(),
    }).eq('id', tier.id)
    setBusy(false)
    if (error) { setMsg({ type: 'error', text: pricingError(error) }); return }
    setMsg({ type: 'success', text: `Tier "${f.tier_name}" saved.` })
    onChanged()
  }
  async function remove() {
    if (!window.confirm(`Delete the tier "${tier.tier_name}"? Companies on it become unassigned.`)) return
    setBusy(true)
    const { error } = await supabase.from('pricing_plan_tiers').delete().eq('id', tier.id)
    setBusy(false)
    if (error) { setMsg({ type: 'error', text: pricingError(error) }); return }
    setMsg({ type: 'success', text: 'Tier deleted.' })
    onChanged()
  }

  const cell = { padding: 4 }
  return (
    <tr style={{ borderBottom: '1px solid #F5F1EA' }}>
      <td style={cell}><input value={f.tier_name} onChange={set('tier_name')} style={inputStyle} /></td>
      <td style={cell}><input type="number" min="1" value={f.max_users} onChange={set('max_users')} placeholder="no limit" style={inputStyle} /></td>
      <td style={cell}><input type="number" min="1" value={f.max_projects} onChange={set('max_projects')} placeholder="no limit" style={inputStyle} /></td>
      <td style={cell}><input type="number" min="0" step="0.01" value={f.monthly_price} onChange={set('monthly_price')} style={inputStyle} /></td>
      <td style={{ ...cell, width: 70 }}><input type="number" value={f.sort_order} onChange={set('sort_order')} style={inputStyle} /></td>
      <td style={{ ...cell, textAlign: 'center' }}><input type="checkbox" checked={f.show_on_landing} onChange={set('show_on_landing')} /></td>
      <td style={{ ...cell, textAlign: 'center' }}><input type="checkbox" checked={f.is_active} onChange={set('is_active')} /></td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        <button type="button" disabled={busy} onClick={save} style={{ ...btn, marginRight: 4 }}>Save</button>
        <button type="button" disabled={busy} onClick={remove} style={{ ...btn, color: '#A32D2D' }}>Delete</button>
      </td>
    </tr>
  )
}
